/**
 * Interactive Help Center — flow data types and role metadata.
 */

export interface FlowBranch {
  label: string;
  description: string;
  outcome: string;
}

export interface FlowStep {
  id: string;
  order: number;
  role: string;
  roleName: string;
  action: string;
  description: string;
  screenPath?: string;
  triggerNext: 'auto' | 'manual';
  /** What happens when it's NOT the happy path. */
  branches?: FlowBranch[];
}

export interface FlowDefinition {
  id: string;
  title: string;
  description: string;
  category: 'work-orders' | 'vendor-p2p' | 'fleet' | 'admin';
  roles: string[];
  steps: FlowStep[];
}

export interface RoleMeta {
  id: string;
  name: string;
  color: string;
  initials: string;
  description: string;
}

export const ROLE_META: Record<string, RoleMeta> = {
  technician:        { id: 'technician',        name: 'Technician',         color: '#3b82f6', initials: 'TC', description: 'Field worker — executes work orders, logs time, reports issues.' },
  supervisor:        { id: 'supervisor',        name: 'Supervisor',         color: '#f59e0b', initials: 'SV', description: 'Team lead — assigns work, approves proposals, reviews invoices.' },
  admin:             { id: 'admin',             name: 'Administrator',      color: '#a855f7', initials: 'AD', description: 'System admin — manages users, settings, fleet lifecycle, impersonation.' },
  vendor_tech:       { id: 'vendor_tech',       name: 'Vendor Tech',        color: '#06b6d4', initials: 'VT', description: 'External vendor field worker — receives assignments, submits estimates.' },
  vendor_supervisor: { id: 'vendor_supervisor', name: 'Vendor Supervisor',  color: '#0891b2', initials: 'VS', description: 'Vendor team lead — dispatches techs, submits invoices.' },
  vendor_admin:      { id: 'vendor_admin',      name: 'Vendor Admin',       color: '#0e7490', initials: 'VA', description: 'Vendor company admin — manages team, compliance docs.' },
  customer_operator: { id: 'customer_operator', name: 'Customer / Operator', color: '#22c55e', initials: 'CO', description: 'Department vehicle owner — approves repair estimates.' },
  p2p_reviewer:      { id: 'p2p_reviewer',      name: 'P2P Reviewer',       color: '#f59e0b', initials: 'PR', description: 'Validates vendor invoices via three-way match.' },
  garage_coordinator:{ id: 'garage_coordinator', name: 'Garage Coordinator', color: '#059669', initials: 'GC', description: 'Manages fleet vendor scheduling and staging appointments.' },
  garage_lead:       { id: 'garage_lead',       name: 'Garage Lead',        color: '#047857', initials: 'GL', description: 'Approves fleet repair estimates under $500 directly; escalates higher amounts to the vehicle owner.' },
  garage_cls:        { id: 'garage_cls',        name: 'Garage CLS',         color: '#065f46', initials: 'CL', description: 'Washes and refuels returned vehicles before customer pickup.' },
  fleet_technician:  { id: 'fleet_technician',  name: 'Fleet Technician',   color: '#2563eb', initials: 'FT', description: 'Evaluates vehicles, coordinates vendor handoffs, schedules return appointments.' },
  fleet_admin:       { id: 'fleet_admin',       name: 'Fleet Admin',        color: '#059669', initials: 'FA', description: 'Manages vehicle lifecycle — insurance, transfers, disposals.' },
  finance:           { id: 'finance',           name: 'Finance',            color: '#8b5cf6', initials: 'FN', description: 'Approves cost proration on vehicle transfers.' },
};

export const CATEGORY_LABELS: Record<string, string> = {
  'work-orders': 'Work Orders',
  'vendor-p2p': 'Vendor & P2P',
  fleet: 'Fleet',
  admin: 'Admin',
};
